import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  serverTimestamp, 
  DocumentReference, 
  query, 
  where, 
  getDocs, 
  Timestamp, 
  limit, 
  orderBy,
  doc,
  updateDoc,
  increment,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { Idea } from '../types/idea-machine';
import { seedsService } from './seeds';

export interface IdeaSubmission {
  seedId: string;
  userEmail: string;
  content: string[];
  timeSpent: number;
  timestamp?: Date;
}

const IDEAS_COLLECTION = 'ideas';

export const ideasService = {
  async submitIdeas(submission: IdeaSubmission): Promise<DocumentReference> {
    if (!submission.seedId) {
      throw new Error('Missing seed ID');
    }
    if (!submission.userEmail) {
      throw new Error('Missing user email');
    }
    if (!submission.content.length) {
      throw new Error('No ideas provided');
    }

    try {
      console.log('Starting idea submission process...', {
        seedId: submission.seedId,
        userEmail: submission.userEmail,
        ideaCount: submission.content.length,
        timeSpent: submission.timeSpent
      });

      // First, verify the seed exists
      const seedsRef = collection(db, 'seeds');
      const seedQuery = query(seedsRef, where('active', '==', true));
      const seedSnapshot = await getDocs(seedQuery);
      const seed = seedSnapshot.docs.find(doc => doc.id === submission.seedId);

      if (!seed) {
        throw new Error('Seed not found');
      }

      const ideasRef = collection(db, IDEAS_COLLECTION);
      
      const docData = {
        seedId: submission.seedId,
        userEmail: submission.userEmail,
        content: submission.content,
        timeSpent: submission.timeSpent,
        timestamp: serverTimestamp(),
        createdAt: new Date().toISOString(), // Fallback timestamp
        seedPrompt: seed.data().prompt,
        seedCategory: seed.data().category,
        isPublic: false // Private by default
      };

      console.log('Submitting to Firestore:', { collection: IDEAS_COLLECTION, data: docData });
      const docRef = await addDoc(ideasRef, docData);
      
      if (!docRef.id) {
        throw new Error('Failed to get document ID after submission');
      }

      // Mark the seed as used for this user
      await seedsService.markSeedAsUnavailable(submission.seedId, submission.userEmail, 'used');

      console.log('Ideas submitted successfully, doc ID:', docRef.id);
      return docRef;
    } catch (error) {
      console.error('Error in submitIdeas:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to submit ideas: ${error.message}`);
      }
      throw new Error('Failed to submit ideas: Unknown error');
    }
  },

  async getUserIdeas(userEmail: string, page: number = 1, limit: number = 10): Promise<{ ideas: Idea[], total: number }> {
    try {
      console.log(`Fetching ideas for user: ${userEmail}, page: ${page}, limit: ${limit}`);
      const ideasRef = collection(db, IDEAS_COLLECTION);
      const q = query(ideasRef, where('userEmail', '==', userEmail));
      
      const snapshot = await getDocs(q);
      const total = snapshot.size;

      const ideas = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          seedId: data.seedId,
          userEmail: data.userEmail,
          content: data.content,
          timeSpent: data.timeSpent,
          timestamp: (data.timestamp as Timestamp)?.toDate() || new Date(data.createdAt),
          seedPrompt: data.seedPrompt,
          seedCategory: data.seedCategory,
          isPublic: data.isPublic || false
        };
      });

      ideas.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      const paginatedIdeas = ideas.slice((page - 1) * limit, page * limit);

      console.log('Fetched ideas:', paginatedIdeas);
      return { ideas: paginatedIdeas, total };
    } catch (error) {
      console.error('Error fetching user ideas:', error);
      throw error;
    }
  },

  async getAllIdeas(limitCount: number = 500): Promise<Idea[]> {
    try {
      console.log(`Fetching all ideas (limit: ${limitCount})`);
      const ideasRef = collection(db, IDEAS_COLLECTION);
      // Note: Filtering client-side to avoid missing index composite query error
      // (where isPublic + orderBy timestamp requires standard ASC/DESC index which might be missing)
      const q = query(
        ideasRef, 
        orderBy('timestamp', 'desc'), 
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      
      const ideas = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            seedId: data.seedId,
            userEmail: data.userEmail,
            content: data.content,
            timeSpent: data.timeSpent,
            timestamp: (data.timestamp as Timestamp)?.toDate() || new Date(data.createdAt),
            seedPrompt: data.seedPrompt,
            seedCategory: data.seedCategory,
            isPublic: data.isPublic || false
          };
        })
        .filter(idea => idea.isPublic); // Client-side filter

      return ideas;
    } catch (error) {
      console.error('Error fetching all ideas:', error);
      throw error;
    }
  },

  toggleUpvote: async (ideaId: string, userEmail: string): Promise<void> => {
    try {
      const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
      const ideaDoc = await getDocs(query(collection(db, IDEAS_COLLECTION), where('__name__', '==', ideaId)));
      
      if (ideaDoc.empty) throw new Error('Idea not found');
      
      const ideaData = ideaDoc.docs[0].data();
      const upvotedBy = ideaData.upvotedBy || [];
      const hasUpvoted = upvotedBy.includes(userEmail);

      if (hasUpvoted) {
        await updateDoc(ideaRef, {
          upvotes: increment(-1),
          upvotedBy: arrayRemove(userEmail)
        });
      } else {
        await updateDoc(ideaRef, {
          upvotes: increment(1),
          upvotedBy: arrayUnion(userEmail)
        });
      }
    } catch (error) {
      console.error('Error toggling upvote:', error);
      throw error;
    }
  },

  addComment: async (ideaId: string, userEmail: string, text: string): Promise<void> => {
    try {
      const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
      const newComment = {
        id: crypto.randomUUID(),
        userEmail,
        text,
        timestamp: new Date()
      };

      await updateDoc(ideaRef, {
        comments: arrayUnion(newComment)
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  },

  togglePrivacy: async (ideaId: string, isPublic: boolean): Promise<void> => {
    try {
      const ideaRef = doc(db, IDEAS_COLLECTION, ideaId);
      await updateDoc(ideaRef, {
        isPublic: isPublic
      });
      console.log(`Idea ${ideaId} privacy toggled to: ${isPublic}`);
    } catch (error) {
      console.error('Error toggling privacy:', error);
      throw error;
    }
  }
};
